import {
  QUOTE_APPROVAL_MESSAGE_KINDS,
  QUOTE_CHANGE_KINDS,
  NEW_QUOTE_REQUEST_REASONS,
  QUOTE_DECLINERS,
  QUOTE_CLAIM_REFUSALS,
  QUOTE_EMAIL_SEND_KINDS,
  QUOTE_SOURCES,
  BUYER_DETAIL_GAPS,
  QUOTE_BUILDER_LISTING_READINESSES,
  QUOTE_TERMS_OUTCOMES,
} from "../../constants/quotes";
import { type Infer, v } from "convex/values";

export const quoteStatuses = [
  "draft",
  "pending_review",
  "sent",
  "viewed",
  "accepted",
  "rejected",
  "expired",
  "no_response",
  "converted",
  "cancelled",
  "paid",
] as const;

export const quoteStatusValidator = v.union(
  ...quoteStatuses.map((status) => v.literal(status)),
);

export type QuoteStatus = Infer<typeof quoteStatusValidator>;

export const QUOTE_STATUS = {
  DRAFT: "draft",
  PENDING_REVIEW: "pending_review",
  SENT: "sent",
  VIEWED: "viewed",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  EXPIRED: "expired",
  NO_RESPONSE: "no_response",
  CONVERTED: "converted",
  CANCELLED: "cancelled",
  PAID: "paid",
} as const satisfies Record<string, QuoteStatus>;

export const QUOTE_OPEN_STATUSES = [
  QUOTE_STATUS.DRAFT,
  QUOTE_STATUS.PENDING_REVIEW,
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.VIEWED,
  QUOTE_STATUS.ACCEPTED,
] as const;

export const QUOTE_TERMINAL_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QUOTE_STATUS.REJECTED,
  QUOTE_STATUS.EXPIRED,
  QUOTE_STATUS.NO_RESPONSE,
  QUOTE_STATUS.CONVERTED,
  QUOTE_STATUS.CANCELLED,
  QUOTE_STATUS.PAID,
]);

export const QUOTE_FOLLOW_UP_STATUSES = [
  QUOTE_STATUS.PENDING_REVIEW,
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.VIEWED,
] as const;

export const QUOTE_AWAITING_BUYER_STATUSES = [
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.VIEWED,
] as const;

export const QUOTE_BUYER_VISIBLE_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.VIEWED,
  QUOTE_STATUS.ACCEPTED,
  QUOTE_STATUS.REJECTED,
  QUOTE_STATUS.EXPIRED,
  QUOTE_STATUS.CONVERTED,
  QUOTE_STATUS.PAID,
]);

const QUOTE_BUYER_CLOSED_AFTER_SEND: ReadonlySet<QuoteStatus> = new Set([
  QUOTE_STATUS.CANCELLED,
  QUOTE_STATUS.NO_RESPONSE,
]);

export const QUOTE_BUYER_QUERY_STATUSES: ReadonlyArray<QuoteStatus> = [
  ...QUOTE_BUYER_VISIBLE_STATUSES,
  ...QUOTE_BUYER_CLOSED_AFTER_SEND,
];

export function isBuyerVisibleQuote(quote: {
  status: QuoteStatus;
  sentAt?: number;
}): boolean {
  if (QUOTE_BUYER_VISIBLE_STATUSES.has(quote.status)) return true;
  return (
    QUOTE_BUYER_CLOSED_AFTER_SEND.has(quote.status) &&
    quote.sentAt !== undefined
  );
}

export function isBuyerListedQuote(quote: {
  status: QuoteStatus;
  sentAt?: number;
  supersededByQuoteId?: string;
}): boolean {
  return !quote.supersededByQuoteId && isBuyerVisibleQuote(quote);
}

export const paymentTermsValidator = v.union(
  v.literal("prepay"),
  v.literal("payment_on_delivery"),
  v.literal("due_on_receipt"),
  v.literal("net_7"),
  v.literal("net_15"),
  v.literal("net_30"),
  v.literal("net_45"),
  v.literal("net_60"),
  v.literal("net_90"),
  v.literal("custom"),
);
export type PaymentTerms = Infer<typeof paymentTermsValidator>;

export const quotePaymentTermsValidator = paymentTermsValidator;
export type QuotePaymentTerms = PaymentTerms;

export const freightSourceValidator = v.union(
  v.literal("uber_api"),
  v.literal("manual_override"),
  v.literal("pickup"),
);

export const quoteApprovalStatusValidator = v.union(
  v.literal("pending"),
  v.literal("approved"),
  v.literal("rejected"),
);

export const freightHandlingValidator = v.union(
  v.literal("baked_in"),
  v.literal("line_item"),
  v.literal("included"),
  v.literal("charged_to_buyer"),
  v.literal("pickup"),
  v.literal("vendor_delivery"),
);

export const fulfillmentTypeValidator = v.union(
  v.literal("pickup"),
  v.literal("delivery"),
);

export const quoteSourceValidator = v.union(
  ...QUOTE_SOURCES.map((source) => v.literal(source)),
);

export const buyerDetailGapValidator = v.union(
  ...BUYER_DETAIL_GAPS.map((gap) => v.literal(gap)),
);

export const quoteSendMethodValidator = v.union(
  v.literal("standard_email"),
  v.literal("stripe_invoice"),
);

export const quoteEmailSendKindValidator = v.union(
  ...QUOTE_EMAIL_SEND_KINDS.map((kind) => v.literal(kind)),
);

export type QuoteEmailSendKind = Infer<typeof quoteEmailSendKindValidator>;

export const quoteTermsOutcomeValidator = v.union(
  ...QUOTE_TERMS_OUTCOMES.map((outcome) => v.literal(outcome)),
);

export const quoteClaimRefusalValidator = v.union(
  ...QUOTE_CLAIM_REFUSALS.map((reason) => v.literal(reason)),
);

export const newQuoteRequestReasonValidator = v.union(
  ...NEW_QUOTE_REQUEST_REASONS.map((reason) => v.literal(reason)),
);

export const quoteDeclinerValidator = v.union(...QUOTE_DECLINERS.map((decliner) => v.literal(decliner)));

export const quoteDenialReasonValidator = v.union(
  v.literal("price_too_high"),
  v.literal("wrong_product_fit"),
  v.literal("timing_not_right"),
  v.literal("found_elsewhere"),
  v.literal("budget_constraints"),
  v.literal("project_cancelled"),
  v.literal("other"),
);

export type QuoteDenialReason = Infer<typeof quoteDenialReasonValidator>;

export const quotePaymentResponsibilityValidator = v.union(
  v.literal("vendor_is_seller_of_record"),
  v.literal("repackify_is_seller_of_record"),
);

export const quotePaymentStatusValidator = v.union(
  v.literal("pending"),
  v.literal("paid"),
  v.literal("failed"),
  v.literal("refunded"),
);

export const quotePayoutStatusValidator = v.union(
  v.literal("pending"),
  v.literal("paid"),
  v.literal("failed"),
);

export function isImmediatePaymentTerms(terms: PaymentTerms): boolean {
  return terms === "prepay" || terms === "due_on_receipt";
}

export function isZeroDayPaymentTerms(terms: PaymentTerms): boolean {
  return terms !== "custom" && resolveTermsDays(terms) === 0;
}

export function resolveTermsDays(
  terms: PaymentTerms,
  customDays?: number,
): number {
  switch (terms) {
    case "prepay":
    case "due_on_receipt":
    case "payment_on_delivery":
      return 0;
    case "net_7":
      return 7;
    case "net_15":
      return 15;
    case "net_30":
      return 30;
    case "net_45":
      return 45;
    case "net_60":
      return 60;
    case "net_90":
      return 90;
    case "custom":
      return customDays ?? 30;
  }
}

export const quoteChangeKindValidator = v.union(
  ...QUOTE_CHANGE_KINDS.map((kind) => v.literal(kind)),
);

export const quoteRequestedChangeValidator = v.object({
  kind: quoteChangeKindValidator,
  note: v.string(),
});

export type QuoteRequestedChange = Infer<typeof quoteRequestedChangeValidator>;

export const quoteApprovalMessageKindValidator = v.union(
  ...QUOTE_APPROVAL_MESSAGE_KINDS.map((kind) => v.literal(kind)),
);

export const quoteBuilderListingReadinessValidator = v.union(
  ...QUOTE_BUILDER_LISTING_READINESSES.map((readiness) => v.literal(readiness)),
);
